import { useCallback, useState } from 'react';
import { Product, PromoAdvice } from '../types/product.types';
import i18n from '../utils/i18n';
import { ComplexPromotionCondition } from '../utils/product.util';
import { useDynamicData } from '../contexts/DataProvider.context';
import { extractComplexPromotion3PFromProducts, getComplexPromotion } from './complexPromotions';
import { config } from '../config/env';

export type ComplexPromotion = {promoAdvice: PromoAdvice[]; loading: boolean};

export default function useCampaignComplexPromotion() {
  const { complexPromotion, setComplexPromotion } = useDynamicData();
  const isFetchRM3FromProductApi = config.LOYALTY_RM3_PROMOTION_FROM_PRODUCT_API;

  const [loading, setLoading] = useState(false);

  const isAllConditionEnable = true;

  const getComplexPromotionV2 = useCallback(
    async (
      productIds: Array<{ shopifyProductId: string; price: string }>,
      storeCodes: string[],
      existingProducts?: Product[],
    ) => {
      if (productIds.length === 0) {
        return;
      }
      setLoading(true);
      try {
        // add loading flag to all products
        let tempComplexPromotion = {
          ...complexPromotion,
        } as Record<string, ComplexPromotion>;
        productIds.forEach((product) => {
          const itemId = product.shopifyProductId.replace('gid://shopify/Product/', '');
          tempComplexPromotion[itemId] = { promoAdvice: [], loading: true };
        });
        setComplexPromotion({
          ...tempComplexPromotion,
        });

        let response: PromoAdvice[] = [];
        // Fetch RM3 promotions from product API if feature flag is enabled
        if (isFetchRM3FromProductApi) {
          try {
            // Use existing products if available, otherwise fetch from API
            if (existingProducts && existingProducts.length > 0) {
              // Use provided products data instead of making API call
              response = extractComplexPromotion3PFromProducts(existingProducts);
            }
          } catch (error) {
            console.error('Error fetching from product API, falling back to getComplexPromotion API:', error);
            // Fall back to old API if product API fails
            response = await getComplexPromotion({
              productIds,
              storeCodes,
              lang: i18n.language,
              conditionFilter: isAllConditionEnable ? ComplexPromotionCondition.ALL : ComplexPromotionCondition.AND,
              version: 'v2',
            });
          }
        } else {
          response = await getComplexPromotion({
            productIds,
            storeCodes,
            lang: i18n.language,
            conditionFilter: isAllConditionEnable ? ComplexPromotionCondition.ALL : ComplexPromotionCondition.AND,
            version: 'v2',
          });
        }
        // Map each promotion to its corresponding product
        response.forEach((promo) => {
          // Try to find the matching product by itemId
          const matchingProduct = productIds.find((product) => {
            const processedId = product.shopifyProductId.replace('gid://shopify/Product/', '');
            return processedId === promo.itemId;
          });

          if (matchingProduct) {
            // Exact match found - map to the correct product
            const key = matchingProduct.shopifyProductId.replace('gid://shopify/Product/', '');
            if (tempComplexPromotion[key]) {
              tempComplexPromotion[key].promoAdvice.push(promo);
              tempComplexPromotion[key].loading = false;
            }
          } else {
            // No exact match - API returns makroId instead of shopify product ID
            // Store the promotion with the itemId as key (this is the makroId)
            tempComplexPromotion[promo.itemId] = {
              promoAdvice: [promo],
              loading: false,
            };
          }
        });

        // set loading false on not exist
        productIds.forEach((product) => {
          const itemId = product.shopifyProductId.replace('gid://shopify/Product/', '');
          if (tempComplexPromotion[itemId]) {
            tempComplexPromotion[itemId].loading = false;
          }
        });

        setComplexPromotion((prevComplexPromotion) => ({
          ...prevComplexPromotion,
          ...tempComplexPromotion,
        }));
      } catch (error) {
        console.error('Error fetching complex promotions:', error);
      } finally {
        setLoading(false);
      }
    },
    [complexPromotion, setComplexPromotion, isAllConditionEnable, isFetchRM3FromProductApi],
  );

  return { getComplexPromotionV2, complexPromotion, loading };
}
